import { memo, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { setCurrentQuestionInfo } from '@actions/quizTaking';
import { Fonts, Spacings } from '@styles/index';
import { scrollIntoViewIfNeeded } from '@cont/Quiz/utils';
import useMaxAttemptQuestions from '@cont/Quiz/Taking/Common/useMaxAttemptQuestions';
import Tooltip from '@base/Tooltip/Tooltip';
import useQuestion from '@cont/Quiz/Taking/Objective/Question/useQuestion';
import useQuestionStatus from './useQuestionStatus';

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${Fonts.ULTRA_SMALL};
  position: relative;
  cursor: ${({ $isLocked }) => ($isLocked ? 'not-allowed' : 'pointer')};
  opacity: ${({ $isLocked }) => ($isLocked ? 0.3 : 1)};

  transform: background 400ms ease-in;
  height: ${Spacings.SPACING_8B};
  width: ${Spacings.SPACING_8B};
  border-radius: ${Spacings.SPACING_6};

  ${({ $isAttempted, $isMarked, $isUnanswered }) => {
    if ($isMarked) {
      return `
        border-radius:50%;
        font-weight: ${Fonts.HEAVY};
        color: var(--color-purple-dark);
        background: rgba(114, 96, 224, 0.15);
      `;
    }
    if ($isAttempted) {
      return `
      color: var(--color-i-green);
        font-weight: ${Fonts.HEAVY};
        background: var(--color-i-light-green);
        border-radius:${Spacings.SPACING_4B} ${Spacings.SPACING_4B} ${Spacings.SPACING_6} ${Spacings.SPACING_6};
      `;
    }

    if ($isUnanswered) {
      return `
      color: var(--color-red-fill-focus);
        font-weight: ${Fonts.HEAVY};
        background:rgba(235, 87, 87, 0.15);
        border-radius: ${Spacings.SPACING_6} ${Spacings.SPACING_6} ${Spacings.SPACING_4B} ${Spacings.SPACING_4B};
      `;
    }

    return `
      color:var(--color-base-fill);
      background: var(--color-base-1);
    `;
  }}
  border: ${({ $isSelected, $notVisited }) => {
    if ($isSelected) {
      return '1px solid var(--color-text-primary)';
    }
    if ($notVisited) {
      return '1px solid var(--color-divider)';
    }
    return 'none';
  }};
`;

const MarkedIcon = styled.div`
  position: absolute;
  width: ${Spacings.SPACING_3B};
  border-radius: 50%;
  height: ${Spacings.SPACING_3B};
  top: ${Spacings.SPACING_0B};
  right: -${Spacings.SPACING_3};
  transform: all 400ms ease-in;
  border: 1px solid var(--color-base-1);
  background-color: var(--color-i-green);
`;

const TooltipText = styled.div`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H6};
  line-height: 21px;
`;

const Question = ({ rank, sectionUID, questionUID, isLocked }) => {
  const dispatch = useDispatch();
  const blockRef = useRef(null);
  const [isClicked, setIsClicked] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const {
    isSelected,
    isAttempted,
    isMarked,
    isMarkedAndAttempted,
    isUnanswered
  } = useQuestionStatus(sectionUID, questionUID);

  const { currentQuestion } = useQuestion();

  const { isQuestionAttemptable } = useMaxAttemptQuestions(sectionUID);

  useEffect(() => {
    if (!isSelected && isClicked) {
      setIsClicked(false);
    }
  }, [isSelected, isClicked]);

  useEffect(() => {
    if (isSelected && !isClicked && blockRef.current) {
      scrollIntoViewIfNeeded({ target: blockRef.current, paddingInPixel: 50 });
    }
  }, [isSelected, isClicked]);

  const handleOnClick = () => {
    if (isLocked) {
      return;
    }

    setIsClicked(true);
    if (currentQuestion.uid !== questionUID) {
      dispatch(
        setCurrentQuestionInfo({
          sectionUID,
          questionUID
        })
      );
    }
  };

  const onQuestionMouseOver = () => {
    if (!isAttempted && !isQuestionAttemptable) {
      setShowTooltip(true);
    }
  };

  const onQuestionMouseOut = () => {
    setShowTooltip(false);
  };

  return (
    <>
      <Tooltip
        padding={Spacings.SPACING_4B}
        maxWidth="212px"
        title={
          <TooltipText>
            Clear a previously answered response from this section to attempt
            this
          </TooltipText>
        }
        open={showTooltip}
        borderRadius={Spacings.SPACING_2B}
        placement="top"
      >
        <Wrapper
          ref={blockRef}
          $isSelected={isSelected}
          $isAttempted={isAttempted}
          $isUnanswered={isUnanswered}
          $isMarked={isMarked}
          $notVisited={!isUnanswered && !isMarkedAndAttempted}
          $isLocked={isLocked}
          onClick={handleOnClick}
          onMouseOver={onQuestionMouseOver}
          onMouseOut={onQuestionMouseOut}
        >
          {rank}
          {isMarkedAndAttempted ? <MarkedIcon /> : null}
        </Wrapper>
      </Tooltip>
    </>
  );
};

export default memo(Question);

Question.propTypes = {
  rank: PropTypes.number.isRequired,
  sectionUID: PropTypes.string.isRequired,
  questionUID: PropTypes.string.isRequired,
  isLocked: PropTypes.bool.isRequired
};
